import { Typography } from '@material-ui/core';
import { AttachFile, Cancel, CheckCircle } from '@material-ui/icons';
import { errorMessages } from 'common/errorMessages.strings';
import { tipoDocumentoS3Descriptions } from 'common/genericDescriptions';
import { CadastroNCByFile, TipoDocumentoS3, FuncaoParticipante, GrupoAcesso, InvestidorResponse, Modulos, Perfil } from 'common/genericTypes';
import { Button, MdSpace, SmSpace } from 'm2m.components';
import React, { Fragment, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DataGridPro, GridCellParams, GridSortDirection, GridSortModel, GridColDef } from '@mui/x-data-grid-pro';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { sanitizeAlphanumericCnpj } from 'utils/cnpjUtils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import _ from 'lodash';
import { addAlert } from 'store/modules/snackBar';
import { FileService } from 'services/fileService';
import { Attachment as AttachmentType, InvestidorEmLoteProps, InvestidorParsedData, defaultAcceptableFileTypes } from './investidorEmLoteTypes';
import { useHistory } from 'react-router-dom';

enum ProcessingResult {
    SUCCESS_TOTAL = 'SUCCESS_TOTAL',
    SUCCESS_PARTIAL = 'SUCCESS_PARTIAL',
    FAILURE_TOTAL = 'FAILURE_TOTAL',
    ERROR_GENERIC = 'ERROR_GENERIC'
}

interface ProcessingOutcome {
    result: ProcessingResult;
    shouldSaveFile: boolean;
    message: string;
    severity: 'success' | 'warning' | 'error';
    shouldRedirect: boolean;
}

const ProcessingUtils = {
    analyzeProcessingResult: (
        totalRecords: number,
        failedRecords: any[],
        formattedData: any[],
        entityType: 'fundo' | 'investidor'
    ): ProcessingOutcome => {
        const uniqueFailedIds = new Set(
            failedRecords.map(f => 
                entityType === 'fundo' 
                    ? (f?.fundoInvestidor?.cnpjFundo || f?.fundo?.cnpjFundo)
                    : f?.investidor?.cpfCnpj
            ).filter(Boolean)
        );
        
        const failedRecordsInFile = formattedData.filter(record => 
            entityType === 'fundo' 
                ? uniqueFailedIds.has(record.cnpjFundo)
                : uniqueFailedIds.has(record.cpfCnpj)
        ).length;
        
        const successRecords = totalRecords - failedRecordsInFile;
        const entityName = entityType === 'fundo' ? 'fundo(s) investidor(es)' : 'investidor(es)';
        
        if (failedRecordsInFile === 0 && successRecords > 0) {
            return {
                result: ProcessingResult.SUCCESS_TOTAL,
                shouldSaveFile: true,
                message: 'Arquivo enviado com sucesso!',
                severity: 'success',
                shouldRedirect: true
            };
        }
        
        if (failedRecordsInFile > 0 && successRecords > 0) {
            return {
                result: ProcessingResult.SUCCESS_PARTIAL,
                shouldSaveFile: true,
                message: `Arquivo processado parcialmente: ${successRecords} ${entityName} cadastrado(s) com sucesso, ${failedRecordsInFile} com erro.`,
                severity: 'warning',
                shouldRedirect: false
            };
        }
        
        if (failedRecordsInFile === totalRecords) {
            return {
                result: ProcessingResult.FAILURE_TOTAL,
                shouldSaveFile: false,
                message: `Falha ao inserir todos os ${failedRecordsInFile} ${entityName}. Nenhum registro foi cadastrado.`,
                severity: 'error',
                shouldRedirect: false
            };
        }
        
        return {
            result: ProcessingResult.ERROR_GENERIC,
            shouldSaveFile: false,
            message: 'Falha ao enviar arquivo.',
            severity: 'error',
            shouldRedirect: false
        };
    },

    processGridDataWithDuplicates: (
        formattedData: any[],
        failedRecords: any[],
        entityType: 'fundo' | 'investidor'
    ) => {
        if (failedRecords.length === 0) {
            return formattedData;
        }

        const failedIdsMap = new Map<string, { error: string, count: number }>();
        failedRecords.forEach(failed => {
            const id = entityType === 'fundo' 
                ? (failed?.fundoInvestidor?.cnpjFundo || failed?.fundo?.cnpjFundo)
                : failed?.investidor?.cpfCnpj;
            
            if (id) {
                if (failedIdsMap.has(id)) {
                    const existing = failedIdsMap.get(id)!;
                    failedIdsMap.set(id, { ...existing, count: existing.count + 1 });
                } else {
                    failedIdsMap.set(id, { error: failed.error, count: 1 });
                }
            }
        });

        const idCountInFile = new Map<string, number>();
        formattedData.forEach(record => {
            const id = entityType === 'fundo' ? record.cnpjFundo : record.cpfCnpj;
            if (id) {
                idCountInFile.set(id, (idCountInFile.get(id) || 0) + 1);
            }
        });

        return formattedData.map((record, index) => {
            const id = entityType === 'fundo' ? record.cnpjFundo : record.cpfCnpj;
            const failedInfo = failedIdsMap.get(id);
            
            if (failedInfo) {
                const totalOccurrencesInFile = idCountInFile.get(id) || 1;
                const failedOccurrences = failedInfo.count;
                const isFirstOccurrence = formattedData.findIndex(f => 
                    entityType === 'fundo' ? f.cnpjFundo === id : f.cpfCnpj === id
                ) === index;
                const shouldMarkAsFailed = failedOccurrences >= totalOccurrencesInFile || !isFirstOccurrence;
                
                if (shouldMarkAsFailed) {
                    return {
                        ...record,
                        valido: false,
                        mensagemErro: record.mensagemErro || failedInfo.error || 'Falha ao inserir no sistema.',
                        failedInBackend: true
                    };
                } else {
                    return {
                        ...record,
                        successfullyRegistered: true
                    };
                }
            }

            if (record.valido) {
                return {
                    ...record,
                    successfullyRegistered: true
                };
            }

            return record;
        });
    }
};

const InvestidorEmLote: React.FC<InvestidorEmLoteProps> = (props) => {
    const { overrideAcceptableFileTypes } = props;
    const history = useHistory();
    const [highlight, setHightlight] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [attachments, setAttachments] = useState<Array<AttachmentType>>([]);
    const [file, setFile] = useState<File | undefined>();
    const [formattedData, setFormattedData] = useState<Array<InvestidorParsedData>>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [pageSize, setPageSize] = React.useState(20);
    const [failedInvestidores, setFailedInvestidores] = useState<{ investidor: InvestidorResponse, error: string }[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);

    const isUsuarioMaster = useSelector((state: RootState) => state.auth.usuario.participante.perfil) === Perfil.Master;
    const isUsuarioOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
    const isUsuarioCadastroOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes;
    const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil) === Perfil.Leitura;
    const isUsuarioEscrita = useSelector((state: RootState) => state.auth.usuario.participante.perfil) === Perfil.Escrita;
    const isParticipanteCustodiante = useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Custodiante;
    const participante = useSelector((state: RootState) => state.auth.usuario.participante);
    const notaComercialByFileApi = useSelector((state: RootState) => state.notaComercialByFileApi);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'dataResponse',
            sort: 'desc' as GridSortDirection,
        },
    ]);

    const templateHeaders = [
        'CPF CNPJ',
        'Razão Social',
        'Email',
        'Nacionalidade',
        'Telefones',
        'Politicamente Exposto',
        'Data Nascimento',
        'Renda Mensal Declarada',
        'Patrimonio Declarado',
        'Atividade Principal / Profissão',
        'CNPJ Custodiante',
        'CEP',
        'Logradouro',
        'Complemento',
        'Bairro',
        'Cidade',
        'UF',
        'Numero'
    ];

    const headerMap: { [key: string]: string } = {
        'CPF CNPJ': 'cpfCnpj',
        'Razão Social': 'razaoSocial',
        'Email': 'email', // OPCIONAL
        'Nacionalidade': 'nacionalidade',
        'Telefones': 'telefones',
        'Politicamente Exposto': 'politicamenteExposto', // OPCIONAL
        'Data Nascimento': 'dataNascimento',
        'Renda Mensal Declarada': 'rendaMensalDeclarada', // OPCIONAL
        'Patrimonio Declarado': 'patrimonioDeclarado',
        'Atividade Principal / Profissão': 'atividadePrincipal',
        'CNPJ Custodiante': 'cnpjCustodiante',
        'CEP': 'cep',
        'Logradouro': 'logradouro',
        'Complemento': 'complemento', // OPCIONAL
        'Bairro': 'bairro',
        'Cidade': 'cidade',
        'UF': 'uf',
        'Numero': 'numero',
        'Tipo de Investidor': 'tipoInvestidor',
        'Vinculado ao Custodiante': 'vinculadoCustodiante'
    };

    const dispatch = useDispatch();

    const acceptedFileTypes = !!overrideAcceptableFileTypes?.length
        ? overrideAcceptableFileTypes
        : defaultAcceptableFileTypes;

    const isValidFileType = (files: File[]): boolean => {
        return acceptedFileTypes.some((type) => files.some((file) => file?.type === type));
    };

    const handleFileTypeError = () => {
        props.onError?.(errorMessages.validation.upload.invalidExtension);
        setHightlight(false);
    };

    const openFileDialog = () => {
        if (props.disabled) return;
        inputRef?.current?.click();
    };

    const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setFile(undefined);
        setAttachments([]);
        setFormattedData([]);
        setValidationErrors([]);
        setFailedInvestidores([]);
        if (props.disabled) return;
        let newFiles: File[] = [];
        if (props.multipleFiles) {
            newFiles = Array.from(event.dataTransfer.files);
        } else if (event.dataTransfer.files) {
            newFiles[0] = event.dataTransfer.files[0];
        }

        if (!newFiles.length) return;

        if (!isValidFileType(newFiles)) {
            handleFileTypeError();
            return;
        }

        const newAttachments = newFiles.map((newFile) => {
            return {
                id: props.fileId ?? uuid(),
                file: newFile,
            };
        });
        setAttachments(newAttachments);
        setFile(event.dataTransfer.files[0]);
        setHightlight(false);

        if (newFiles[0]) {
            try {
                await parseAndValidateFile(newFiles[0]);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Erro ao processar arquivo';
                dispatch(addAlert({ severity: 'error', message: errorMessage }));
                props.onError?.(error);
            }
        }
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (props.disabled) return;
        setHightlight(true);
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (props.disabled) return;
        setHightlight(false);
    };

    const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setFile(undefined);
        setAttachments([]);
        setFormattedData([]);
        setValidationErrors([]);
        setFailedInvestidores([]);
        let newFiles: File[] = [];
        if (event.target.files) {
            newFiles[0] = event.target.files?.[0];
        }

        if (!newFiles.length) return;

        if (!isValidFileType(newFiles)) {
            handleFileTypeError();
            return;
        }

        const newAttachments = newFiles.map((newFile) => {
            return {
                id: props.fileId ?? uuid(),
                file: newFile,
            };
        });

        let updatedAttachments: AttachmentType[];
        if (props.fileId && attachments.some(({ id }) => id === props.fileId)) {
            updatedAttachments = attachments.filter((file) => file.id !== props.fileId).concat(newAttachments);
        } else {
            updatedAttachments = attachments.concat(newAttachments);
        }

        props.onChange?.(newAttachments);
        setHightlight(false);
        setAttachments(newAttachments);
        setFile(event.target.files?.[0]);

        if (newFiles[0]) {
            try {
                await parseAndValidateFile(newFiles[0]);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Erro ao processar arquivo';
                dispatch(addAlert({ severity: 'error', message: errorMessage }));
                props.onError?.(error);
            }
        }
    };

    const parseAndValidateFile = async (file: File) => {
        try {
            setIsValidating(true);
            setLoading(true);

            const parsedData = await FileService.parseFile(file, templateHeaders);

            setHeaders(parsedData.headers);

            const investidores: InvestidorParsedData[] = parsedData.rows.map((row) => {
                const investidor: any = {
                    id: uuid()
                };

                parsedData.headers.forEach((header, headerIndex) => {
                    const fieldName = headerMap[header] ?? _.camelCase(header);
                    const cellValue = row[headerIndex];
                    investidor[fieldName] = cellValue !== null && cellValue !== undefined ? String(cellValue) : '';
                    if (header === 'CPF CNPJ' && cellValue) {
                        investidor[fieldName] = padCpfCnpj(cellValue);
                    }
                });

                const errors: string[] = [];

                const optionalHeaders = ['Email', 'Renda Mensal Declarada', 'Patrimonio Declarado', 'Complemento'];
                parsedData.headers.forEach((header) => {
                    if (optionalHeaders.includes(header)) return;
                    const fieldName = headerMap[header] ?? _.camelCase(header);
                    if (!investidor[fieldName]) {
                        errors.push(`${header} é obrigatório`);
                    }
                });

                if (investidor.cpfCnpj && investidor.cpfCnpj.length === 11 && !validateCpf(investidor.cpfCnpj)) {
                    errors.push('CPF inválido');
                }

                if (investidor.cpfCnpj && investidor.cpfCnpj.length === 14 && !validateCnpj(investidor.cpfCnpj)) {
                    errors.push('CNPJ inválido');
                }

                if (investidor.cpfCnpj && /[A-Za-z]/.test(investidor.cpfCnpj) && investidor.cpfCnpj.length !== 14) {
                    errors.push('CNPJ alfanumérico deve ter 14 caracteres');
                }

                if (investidor.email && !validateEmail(investidor.email)) {
                    errors.push('Email inválido');
                }

                if (investidor.cpfCnpj.length === 11 && investidor.nacionalidade && !validateNacionalidade(investidor.nacionalidade)) {
                    errors.push('Nacionalidade inválida');
                }

                if (investidor.telefones && !validateTelefones(investidor.telefones)) {
                    errors.push('Telefones inválidos');
                }

                if (investidor.cpfCnpj.length === 11 && investidor.dataNascimento && !validateDate(investidor.dataNascimento)) {
                    errors.push('Data de nascimento inválida');
                }

                if (investidor.tipoInvestidor && !validateTipoInvestidor(investidor.tipoInvestidor)) {
                    errors.push('Tipo de investidor inválido - Tipos Disponíveis: Qualificado, Profissional, Nenhum');
                }

                if (investidor.politicamenteExposto && investidor.politicamenteExposto.toUpperCase() !== 'SIM' && investidor.politicamenteExposto.toUpperCase() !== 'NÃO') {
                    errors.push('Politicamente exposto inválido - Opções Disponíveis: (Sim, Não)');
                }

                if (investidor.vinculadoCustodiante && investidor.vinculadoCustodiante.toUpperCase() !== 'SIM' && investidor.vinculadoCustodiante.toUpperCase() !== 'NÃO') {
                    errors.push('Vinculado ao custodiante inválido - Opções Disponíveis: (Sim, Não)');
                }

                investidor.valido = errors.length === 0;
                investidor.mensagemErro = errors.join(', ');

                return investidor as InvestidorParsedData;
            });

            setFormattedData(investidores);

            const invalidRecords = investidores.filter(inv => !inv.valido);
            if (invalidRecords.length > 0) {
                setValidationErrors([`${invalidRecords.length} registro(s) com erro de validação.`]);
                dispatch(addAlert({
                    severity: 'warning',
                    message: `Arquivo processado com ${invalidRecords.length} erro(s) de validação. Verifique os detalhes na tabela.`
                }));
            } else if (investidores.length === 0) {
                setValidationErrors(['Arquivo processado sem registros válidos.']);
                dispatch(addAlert({
                    severity: 'warning',
                    message: 'Arquivo processado sem registros válidos.'
                }));
            } else {
                dispatch(addAlert({
                    severity: 'success',
                    message: `Arquivo processado com sucesso! ${investidores.length} registros válidos.`
                }));
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Erro ao processar arquivo';
            setValidationErrors([errorMessage]);
            throw error;
        } finally {
            setIsValidating(false);
            setLoading(false);
        }
    };

    const validateDate = (date: string): boolean => {
        if (!date) return false;

        const parts = date.split('/');
        if (parts.length !== 3) return false;

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) return false;

        const dateObject = new Date(year, month, day);

        if (dateObject.getDate() !== day ||
            dateObject.getMonth() !== month ||
            dateObject.getFullYear() !== year) {
            return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateObject > today) return false;

        const minAgeDate = new Date(today);
        minAgeDate.setFullYear(today.getFullYear() - 18);
        if (dateObject > minAgeDate) return false;

        const maxAgeDate = new Date();
        maxAgeDate.setFullYear(today.getFullYear() - 120);

        if (dateObject < maxAgeDate) return false;

        return true;
    };

    const validateCnpj = (cnpj: string): boolean => {
        if (!cnpj) return false;

        // Remove formatação mantendo letras e dígitos
        let sanitizedCnpj = sanitizeAlphanumericCnpj(cnpj).trim();

        if (sanitizedCnpj.length !== 14) {
            return false;
        }

        // Verifica se os 2 últimos caracteres são numéricos (dígitos verificadores)
        const digitosVerificadores = sanitizedCnpj.substring(12);
        if (!/^\d{2}$/.test(digitosVerificadores)) return false;

        // Verifica se os 12 primeiros são alfanuméricos
        const base = sanitizedCnpj.substring(0, 12);
        if (!/^[A-Z0-9]{12}$/.test(base)) return false;

        // Rejeita CNPJs com todos os caracteres iguais
        if (/^(.)\1{13}$/.test(sanitizedCnpj)) return false;

        const multiplicador1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const multiplicador2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

        // Função para obter valor numérico de cada caractere (dígitos = 0-9, letras = ASCII - 48)
        const getCharValue = (char: string): number => {
            const code = char.charCodeAt(0);
            return code >= 65 ? code - 48 : code - 48;
        };

        let soma = 0;
        for (let i = 0; i < 12; i++) {
            soma += getCharValue(base[i]) * multiplicador1[i];
        }

        let resto = soma % 11;
        resto = resto < 2 ? 0 : 11 - resto;

        const digito1 = resto;
        if (digito1 !== parseInt(digitosVerificadores[0])) return false;

        const baseComDigito1 = base + digito1.toString();
        soma = 0;
        for (let i = 0; i < 13; i++) {
            soma += getCharValue(baseComDigito1[i]) * multiplicador2[i];
        }

        resto = soma % 11;
        resto = resto < 2 ? 0 : 11 - resto;

        const digito2 = resto;
        if (digito2 !== parseInt(digitosVerificadores[1])) return false;

        return true;
    }

    const validateCpf = (cpf: string): boolean => {
        if (!cpf) return false;

        let sanitizedCpf = cpf.replace(/[^\d]/g, '');

        const multiplicador1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
        const multiplicador2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

        sanitizedCpf = sanitizedCpf.trim();

        if (sanitizedCpf.length !== 11) {
            return false;
        }

        const tempCpf = sanitizedCpf.substring(0, 9);

        let soma = 0;
        for (let i = 0; i < 9; i++) {
            soma += parseInt(tempCpf[i]) * multiplicador1[i];
        }

        let resto = soma % 11;
        resto = resto < 2 ? 0 : 11 - resto;

        const digito = resto.toString();

        const tempCpfWithDigit = tempCpf + digito;
        soma = 0;

        for (let i = 0; i < 10; i++) {
            soma += parseInt(tempCpfWithDigit[i]) * multiplicador2[i];
        }

        resto = soma % 11;
        resto = resto < 2 ? 0 : 11 - resto;

        const finalDigits = digito + resto.toString();

        return sanitizedCpf.endsWith(finalDigits);
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateNacionalidade = (nacionalidade: string): boolean => {
        const nacionalidades = [
            "afegao",
            "afega",
            "alemao",
            "alemã",
            "americano",
            "americana",
            "angolano",
            "angolana",
            "argentino",
            "argentina",
            "australiano",
            "australiana",
            "austriaco",
            "austriaca",
            "azerbaijano",
            "azerbaijana",
            "belga",
            "belga",
            "birmanes",
            "birmanesa",
            "boliviano",
            "boliviana",
            "brasileiro",
            "brasileira",
            "bulgaro",
            "bulgara",
            "cabo-verdiano",
            "cabo-verdiana",
            "cambojano",
            "cambojana",
            "canadense",
            "canadense",
            "catariano",
            "catariana",
            "chileno",
            "chilena",
            "chines",
            "chinesa",
            "colombiano",
            "colombiana",
            "costarriquenho",
            "costarriquenha",
            "croata",
            "croata",
            "cubano",
            "cubana",
            "cipriota",
            "cipriota",
            "dinamarques",
            "dinamarquesa",
            "egipcio",
            "egipcia",
            "emiradense",
            "emiradense",
            "equatoriano",
            "equatoriana",
            "eslovaco",
            "eslovaca",
            "esloveno",
            "eslovena",
            "espanhol",
            "espanhola",
            "estoniano",
            "estoniana",
            "etiope",
            "etiope",
            "finlandes",
            "finlandesa",
            "frances",
            "francesa",
            "georgiano",
            "georgiana",
            "grego",
            "grega",
            "guineense",
            "guineense",
            "haitiano",
            "haitiana",
            "holandes",
            "holandesa",
            "hungaro",
            "hungara",
            "indiano",
            "indiana",
            "indonesio",
            "indonesia",
            "ingles",
            "inglesa",
            "iraniano",
            "iraniana",
            "iraquiano",
            "iraquiana",
            "irlandes",
            "irlandesa",
            "islandes",
            "islandesa",
            "israelense",
            "israelense",
            "italiano",
            "italiana",
            "jamaicano",
            "jamaicana",
            "japones",
            "japonesa",
            "jordano",
            "jordana",
            "kosovar",
            "kosovar",
            "letonio",
            "letonia",
            "libanes",
            "libanesa",
            "libio",
            "libia",
            "lituano",
            "lituana",
            "luxemburgues",
            "luxemburguesa",
            "macedonio",
            "macedonia",
            "malaio",
            "malaia",
            "marroquino",
            "marroquina",
            "mexicano",
            "mexicana",
            "mocambicano",
            "mocambicana",
            "montenegrino",
            "montenegrina",
            "namibiano",
            "namibiana",
            "neozelandes",
            "neozelandesa",
            "nicaraguense",
            "nicaraguense",
            "nigeriano",
            "nigeriana",
            "noruegues",
            "norueguesa",
            "omaense",
            "omaense",
            "paquistanes",
            "paquistanesa",
            "palestino",
            "palestina",
            "panamenho",
            "panamenha",
            "paraguaio",
            "paraguaia",
            "peruano",
            "peruana",
            "polones",
            "polonesa",
            "portugues",
            "portuguesa",
            "queniano",
            "queniana",
            "romeno",
            "romena",
            "ruandes",
            "ruandesa",
            "russo",
            "russa",
            "saudita",
            "saudita",
            "senegales",
            "senegalesa",
            "somali",
            "somali",
            "sudanes",
            "sudanesa",
            "sueco",
            "sueca",
            "suico",
            "suica",
            "sul-africano",
            "sul-africana",
            "sul-coreano",
            "sul-coreana",
            "tailandes",
            "tailandesa",
            "timorense",
            "timorense",
            "tunisiano",
            "tunisiana",
            "turco",
            "turca",
            "ucraniano",
            "ucraniana",
            "uruguaio",
            "uruguaia",
            "uzbeque",
            "uzbeque",
            "venezuelano",
            "venezuelana",
            "vietnamita",
            "vietnamita",
            "zambiano",
            "zambiana",
            "zimbabuano",
            "zimbabuana"
        ]

        return nacionalidades.includes(nacionalidade.toLowerCase());
    };

    const validateTelefones = (telefones: string): boolean => {
        if (!telefones) return false;

        const cleanedNumber = telefones.replace(/\D/g, '');

        if (/[^0-9]/.test(cleanedNumber)) {
            return false;
        }

        if (cleanedNumber.length < 10 || cleanedNumber.length > 11) {
            return false;
        }

        const ddd = parseInt(cleanedNumber.substring(0, 2));
        if (ddd < 11 || ddd > 99) {
            return false;
        }

        return true;
    };

    const validateTipoInvestidor = (tipoInvestidor: string): boolean => {
        const tiposInvestidor = ['Qualificado', 'Profissional', 'Nenhum'];
        return tiposInvestidor.includes(tipoInvestidor);
    };

    const padCpfCnpj = (value: string): string => {
        // Remove formatação mantendo letras e dígitos
        const clean = sanitizeAlphanumericCnpj(value);

        // Se contém letras, é CNPJ alfanumérico - não faz padding
        if (/[A-Z]/.test(clean)) {
            return clean.length <= 14 ? clean.padStart(14, '0') : clean;
        }

        // CPF/CNPJ numérico: padding com zeros à esquerda
        const digits = clean.replace(/\D/g, '');
        if (digits.length <= 11) return digits.padStart(11, '0');
        else if (digits.length <= 14) return digits.padStart(14, '0');

        return digits;
    }

    const getBase64 = async (file: File) => {
        let base64File;
        var reader = new FileReader();
        reader.readAsDataURL(file);
        await new Promise((resolve) => {
            reader.onload = () => {
                base64File = reader.result;
                resolve(base64File);
            };
        });
        return reader;
    };

    const uploadFile = async (file: File | undefined) => {
        if (!file) {
            dispatch(addAlert({ severity: 'error', message: 'Nenhum arquivo selecionado.' }));
            return;
        }

        const invalidRecords = formattedData.filter(inv => !inv.valido);
        if (invalidRecords.length > 0) {
            dispatch(addAlert({
                severity: 'error',
                message: 'Não é possível enviar o arquivo com erros de validação. Corrija os erros e tente novamente.'
            }));
            return;
        }

        let shouldSaveFile = false;
        let cadastroMessage = '';
        
        try {
            setLoading(true);
            await notaComercialByFileApi.postNotaComercialCadastroLoteByFile(dispatch, file, true);
            
            shouldSaveFile = true;
            cadastroMessage = 'Arquivo enviado com sucesso!';
            dispatch(addAlert({ severity: 'success', message: cadastroMessage }));
            history.push('/gerenciar/investidores');
            
        } catch (error: any) {
            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    dispatch(addAlert({ severity: 'error', message: error.response.data }));
                    return;
                }

                const failed: { investidor: any, error: string }[] = error.response.data || [];
                setFailedInvestidores(failed);

                const outcome = ProcessingUtils.analyzeProcessingResult(
                    formattedData.length,
                    failed,
                    formattedData,
                    'investidor'
                );

                shouldSaveFile = outcome.shouldSaveFile;
                cadastroMessage = outcome.message;
                
                dispatch(addAlert({ 
                    severity: outcome.severity, 
                    message: outcome.message 
                }));

                if (outcome.shouldRedirect) {
                    history.push('/gerenciar/investidores');
                }
            } else {
                dispatch(addAlert({ severity: 'error', message: 'Falha ao enviar arquivo.' }));
            }
        } finally {
            setLoading(false);
        }
        
        if (shouldSaveFile) {
            try {
                const file2Base64: string = (await getBase64(file)).result as string;
                
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');
                const timestamp = `${year}_${month}_${day}_${hours}${minutes}${seconds}`;
                const fileName = file?.name ?? '';
                const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
                const fileNameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
                const fileNameWithTimestamp = `${fileNameWithoutExtension}_${timestamp}${fileExtension}`;
                
                const notaComercial: CadastroNCByFile = {
                    diretorio: `${participante.id}/request/${tipoDocumentoS3Descriptions[TipoDocumentoS3.CadastroLote]}`,
                    nomeArquivo: fileNameWithTimestamp,
                    arquivo: file2Base64.split(',')[1],
                };
                await notaComercialByFileApi.postNotaComercialByFile(dispatch, notaComercial, true);
            } catch (saveError) {
                console.warn('Erro ao salvar arquivo, mas cadastro foi realizado:', saveError);
            }
        }
    };

    const clearFields = () => {
        setFile(undefined);
        setAttachments([]);
        setFormattedData([]);
        setValidationErrors([]);
        setFailedInvestidores([]);
        setHeaders([]);
        
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        
        dispatch(addAlert({ 
            severity: 'info', 
            message: 'Arquivo limpo com sucesso!' 
        }));
    };

    const getDynamicGridColumns = useCallback((): GridColDef[] => {
        const extraColumns: GridColDef[] = [
            {
                field: 'valido',
                headerName: 'Válido',
                filterable: true,
                sortable: true,
                type: 'boolean',
                renderCell: (params: GridCellParams) => {
                    return params.row.valido
                        ? (<><CheckCircle fontSize="small" color="primary" /></>)
                        : (<><Cancel fontSize="small" color="error" /></>);
                },
                width: 120,
                minWidth: 80,
                maxWidth: 120,
            },
            {
                field: 'mensagemErro',
                headerName: 'Mensagem de erro',
                filterable: true,
                sortable: true,
                width: 300,
                minWidth: 200,
                flex: 1,
            }
        ];
        return extraColumns.concat(
            headers.map<GridColDef>(header => ({
                field: headerMap[header] ?? _.camelCase(header),
                headerName: header,
                filterable: true,
                sortable: true,
                width: 200,
                minWidth: 100,
                flex: 1,
            }))
        );
    }, [headers]);

    const hideCondition =
        (isUsuarioOperacoes && isUsuarioMaster) ||
        (isUsuarioOperacoes && isUsuarioLeitura) ||
        (isParticipanteCustodiante && isUsuarioLeitura) ||
        (isUsuarioCadastroOperacoes && isUsuarioLeitura);

    const showCondition =
        (isUsuarioOperacoes && isUsuarioEscrita) ||
        (isParticipanteCustodiante && isUsuarioEscrita) ||
        (isParticipanteCustodiante && isUsuarioMaster) ||
        (isUsuarioCadastroOperacoes && isUsuarioEscrita) ||
        (isUsuarioCadastroOperacoes && isUsuarioMaster);

    return (
        <Fragment>
            {showCondition && !hideCondition && (
                <>
                    <SmSpace></SmSpace>
                    <div
                        onClick={openFileDialog}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        style={{
                            width: '100%',
                            border: '1px dashed #D7D8D9',
                            borderRadius: '8px',
                            cursor: props.disabled ? 'default' : 'pointer',
                            textAlign: 'center',
                        }}
                    >
                        <MdSpace />
                        <AttachFile fontSize="small" />
                        <Typography color="textSecondary" variant="body2">
                            {highlight
                                ? 'Solte para carregar os arquivos'
                                : `Arraste e solte ou SELECIONE OS DOCUMENTOS`}
                        </Typography>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={acceptedFileTypes.join(',')}
                            multiple={false}
                            style={{ display: 'none' }}
                            onChange={onInputChange}
                        />
                        <MdSpace />
                    </div>

                    <MdSpace />
                </>
            )}

            <DataGridPro
                autoHeight
                localeText={localizedTextsMap}
                componentsProps={componentsPropsValues}
                rows={ProcessingUtils.processGridDataWithDuplicates(formattedData, failedInvestidores, 'investidor')}
                columns={getDynamicGridColumns()}
                components={{
                    Toolbar: CustomToolBarUtils,
                }}
                disableSelectionOnClick
                rowsPerPageOptions={rowsPerPageOptionsValues}
                pagination
                onPageSizeChange={(newPage) => setPageSize(newPage)}
                pageSize={pageSize}
                loading={loading || isValidating}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                getRowClassName={(params) => params.row.valido ? '' : 'error-row'}
                sx={{
                    '& .error-row': {
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                    },
                }}
            />

            <MdSpace />

            <SmSpace />

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    size="small"
                    color='primary'
                    disabled={isValidating || loading || !file || formattedData.length === 0 || validationErrors.length > 0}
                    onClick={() => uploadFile(file)}
                >
                    {loading ? 'Enviando...' : 'Enviar Arquivo'}
                </Button>

                <Button
                    variant="contained"
                    size="small"
                    color='primary'
                    disabled={isValidating || loading || (formattedData.length === 0 && !file && attachments.length === 0)}
                    onClick={clearFields}
                >
                    Limpar Arquivo
                </Button>

                {validationErrors.length > 0 && (
                    <Typography color="error" variant="body2">
                        {validationErrors.join(', ')}
                    </Typography>
                )}
            </div>

            <MdSpace />

            <SmSpace />
        </Fragment>
    );
};

export default InvestidorEmLote;
